import { Box, Grid, Theme, useMediaQuery, useTheme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import BackdropDialog from 'common/components/BackdropDialog';
import React, { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';
import moment from 'moment';
import { DayPickerRangeController } from 'react-dates';
import {
	exportDeliveries,
	getDeliveriesToday,
	getPickUpsToday,
	getMinDeliveryBookingBuffer,
} from './DeliveryRouteExportUtils';
import { LocationType, RentalsType } from 'selectors/RentalsSelectors';
import { useTranslation } from 'services/localization';
import * as RentalsSelectors from 'selectors/RentalsSelectors';

import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { uniqBy, orderBy } from 'lodash';
import Banner from 'components/Banner';
interface Props {
	isModalOpen: boolean;
	onClose: (isModalOpen: boolean) => void;
	bookingsViewLocatioType: 'current' | 'all';
}

const DeliveryExportModal = (props: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const classes = useStyles();
	const { isModalOpen, onClose, bookingsViewLocatioType } = props;

	const rentalsWithDelivery = useSelector(
		RentalsSelectors.rentalsAtSelectedLocationWithDeliveryTo(
			RentalsType.BOOKED,
			LocationType.START,
		),
	);

	const rentalsWithPickUp = useSelector(
		RentalsSelectors.rentalsAtSelectedLocationWithDeliveryFrom(
			RentalsType.BOOKED,
			LocationType.START,
		),
	);

	const activeRentalsWithDelivery = useSelector(
		RentalsSelectors.rentalsAtSelectedLocationWithDeliveryTo(
			RentalsType.ACTIVE,
			LocationType.START,
		),
	);

	const activeRentalsWithPickUp = useSelector(
		RentalsSelectors.rentalsAtSelectedLocationWithDeliveryFrom(
			RentalsType.ACTIVE,
			LocationType.START,
		),
	);

	const allDeliveries = rentalsWithDelivery.concat(uniqBy(activeRentalsWithDelivery, 'id'));
	const allPickUps = rentalsWithPickUp.concat(uniqBy(activeRentalsWithPickUp, 'id'));

	const userLocations = useSelector(UserSelectors.userLocations);
	const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const [startDate, setStartDate] = useState<moment.Moment | null>(null);
	const productData = useSelector(StockSelectors.stockProductsData);
	const activeLocation = useSelector(UserSelectors.userActiveLocation)!;
	const firstDayOfWeek = useSelector(ShopSelectors.getStartDateNumber);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone);
	const shopDeliveryOptions = useSelector(ShopSelectors.shopDeliveryOptions);
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const minBuffer = getMinDeliveryBookingBuffer(shopDeliveryOptions, shopTimezone, openingHours);
	const minBufferDate = moment().add(minBuffer, 'minutes');

	const orderedDeliveries = orderBy(
		allDeliveries,
		(allDeliveries) => allDeliveries.services?.delivery?.to?.startDate,
	);

	const orderedPickUps = orderBy(allPickUps, (allPickUps) => allPickUps.returnTimeNext);

	const firstDeliveryDate = !!allDeliveries[0]
		? moment(orderedDeliveries[0].services?.delivery?.to?.startDate)
		: null;
	const firstPickUpDate = !!allPickUps[0] ? moment(orderedPickUps[0].returnTimeNext) : null;

	const initialFirstDay = !startDate ? firstDeliveryDate || firstPickUpDate : startDate ?? null;

	const isSelectedDayEarly = !!startDate
		? startDate?.isBefore(minBufferDate)
		: initialFirstDay?.isBefore(minBufferDate);

	const handleDateChange = useCallback(
		({ startDate }: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
			setStartDate(startDate?.startOf('day') ?? null);
		},
		[setStartDate],
	);

	const selectedLocations = bookingsViewLocatioType === 'all' ? userLocations : [activeLocation];

	const handleExport = async () => {
		await exportDeliveries({
			rentalsWithDelivery: allDeliveries,
			rentalsWithPickUp: allPickUps,
			startDate,
			productData,
			userLocations: selectedLocations,
			shopDateFormat,
			shopTimezone,
			t,
		});
		onClose(false);
		setStartDate(null);
	};

	const handleClose = (action: 'click_away' | 'cancel' | 'confirm' | 'left-action') => {
		if (action === 'click_away' || action === 'cancel' || action === 'left-action') {
			onClose(false);
			setStartDate(null);
		}
	};

	const hasDeliveryOnDay = (day: moment.Moment) => {
		return (
			getDeliveriesToday(allDeliveries, day).length > 0 ||
			getPickUpsToday(allPickUps, day).length > 0
		);
	};

	const renderDay = (day: moment.Moment) => {
		return (
			<>
				<Box className={classes.dateWrapper}>
					<span className={classes.date}>{day.format('D')}</span>
				</Box>
			</>
		);
	};

	return (
		<BackdropDialog
			isOpen={isModalOpen}
			label={t('delivery.export.modalLabel', 'Export delivery route')}
			onClose={handleClose}
			wideDialog={true}
			body={
				<Grid container spacing={2}>
					<Grid className={classes.centeredPicker}>
						{!isSelectedDayEarly && (
							<Banner
								mb={4}
								variant="warning"
								title={t('delivery.export.warningLabelTitle', 'The delivery list can still change')}
								description={t(
									'delivery.export.warningLabelBody2',
									'Be aware that the list will not update with new delivery orders after exporting it',
								)}
							/>
						)}
						<Box>
							<DayPickerRangeController
								startDate={initialFirstDay}
								endDate={initialFirstDay}
								onDatesChange={handleDateChange}
								minimumNights={0}
								focusedInput={'startDate'}
								onFocusChange={() => {}}
								daySize={smallScreen ? 36 : 45}
								hideKeyboardShortcutsPanel
								numberOfMonths={smallScreen ? 1 : 2}
								renderDayContents={renderDay}
								firstDayOfWeek={firstDayOfWeek}
								isDayBlocked={(startDate) => !hasDeliveryOnDay(startDate!)}
								noBorder={true}
							/>
						</Box>
					</Grid>
				</Grid>
			}
			confirm={{
				label: t('common:actions.export', 'Export'),
				callback: handleExport,
				closeAfterDone: true,
			}}
			cancel={{
				label: t('common:actions.cancel'),
			}}
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dateWrapper: {
			display: 'flex',
			flexFlow: 'column',
			padding: 1,
		},
		dotWrapper: {
			margin: -10,
			padding: 0,
		},
		date: {
			margin: 2.5,
		},
		centeredPicker: {
			margin: '0 auto',
		},
		'@global': {
			'.CalendarDay__blocked_calendar': {
				background: theme.palette.action.disabled,
			},
			'.CalendarDay__blocked_calendar:hover': {
				background: theme.palette.action.hover,
			},
			[`.CalendarDay__selected_start,
        .CalendarDay__selected_start:active,
        .CalendarDay__selected_start:hover,
        .CalendarDay__highlighted_calendar:hover,
        .CalendarDay__selected,
        .CalendarDay__selected:active,
		.CalendarDay__selected:hover,
		.CalendarDay__highlighted_calendar:hover div,
		.CalendarDay__selected div`]: {
				background: theme.palette.primary.main,
				border: `${theme.palette.primary.main} solid 2px`,
				color: theme.palette.primary.contrastText,
			},
		},
	}),
);

export default DeliveryExportModal;
